"use client"
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Rule, validateWithWarnings, ValidationResult } from './formrenderer/utils/Validation';

// Define the shape of props for ValidationProvider
interface ValidationProps {
  children: React.ReactNode;
  rule: Rule;
}

// Define the shape of your context
interface ValidationContextType {
  dataContext: any;
  setDataContext: (dataContext: any) => void,
  errors: ValidationResult;
  warnings: ValidationResult;
  rule: Rule;
  validateData: () => void;
  validateNow: () => ValidationResult;
}

// Create a context for Validation
const ValidationContext = createContext<ValidationContextType>({
  dataContext: undefined,
  setDataContext: () => { },
  errors: {},
  warnings: {},
  rule: {},
  validateData: () => { },
  validateNow: () => ({})
});

// Custom hook to access Validation context
export const useValidation = () => useContext(ValidationContext);

// ValidationProvider component
export const ValidationProvider: React.FC<ValidationProps> = ({ children, rule }) => {
  const [errors, setErrors] = useState<ValidationResult>({});
  const [warnings, setWarnings] = useState<ValidationResult>({});
  const [dataContext, setDataContext] = useState();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Mutable ref for timeout

  const validateNow = useCallback(() => {
    if (rule) {
      const validationResult = validateWithWarnings(dataContext, rule, '');
      setErrors(validationResult.errors);
      setWarnings(validationResult.warnings);
      return validationResult.errors;
    }

    setErrors({});
    setWarnings({});
    return {};
  }, [dataContext, rule]);

  const validateData = useCallback(() => {
    // Clear the previous timeout if it exists
    //console.log('validateData');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (rule) {
      // Set a new timeout for validation
      timeoutRef.current = setTimeout(() => {
        validateNow(); // Execute validation
      }, 500); // Debounce delay (500ms)
    }
  }, [rule, validateNow]);

  // Initial validation when provider mounts or when rule changes
  useEffect(() => {
    if (dataContext && rule) {
      validateData();
    }
  }, [dataContext, rule, validateData]); // Validation might still depend on the dataContext even after initialization

  return (
    <ValidationContext.Provider value={{ errors, warnings, rule, validateData, validateNow, dataContext, setDataContext }}>
      {children}
    </ValidationContext.Provider>
  );
};
